import React, { useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useMutation } from "react-query";
import {
  Banner,
  Button,
  Input,
  PageTitle,
  PageWrapper,
  Spinner,
} from "@cinesys/ui/components";
import { Check, Search, X } from "react-feather";
import FeatureGate from "../shared/FeatureGate";
import NotFoundPage from "../../components/NotFoundPage";
import { cs_metadata_viewerQueryKeys, useItemMetadata } from "./service";
import {
  buildComponents,
  useCollapsedControl,
  useEditControl,
  MetadataViewerSection,
} from "@cinesys/experience-ui/components";

/**
 * Route wrapper for the ported metadata viewer — the "new chrome" this port genuinely needs.
 * Cantemo has no counterpart to copy here: its own top-level wiring
 * (`cs_metadata_viewer/features/itemPanel.tsx`) is Portal DOM-injection glue — jQuery selectors
 * scraping Portal's item-page tab bar, `window.cntmo`/`window.cs_react` globals, a legacy-form
 * scrape-and-click fallback — none of which has anywhere to land in a standalone SPA. Same
 * carve-out reasoning plan §0 gives the app shell ("no Cantemo counterpart to copy from").
 *
 * The actual rendering pieces (`MetadataViewerSection`, `useEditControl`/`useCollapsedControl`,
 * `buildComponents`) are the SAME verbatim `cs_metadata_viewer` copy `cs_item_browser`'s own
 * docked metadata panel uses (`@cinesys/experience-ui/components`, see docs/decisions.md's
 * 2026-09-01 entry) — imported from there rather than duplicated here, so there is exactly one
 * copy of Cantemo's `cs_metadata_viewer` in this repo. This file's job is only to fetch, wire
 * edit/collapsed state, and render the same composition `MetadataViewerModal.tsx`'s non-timed
 * branch uses, trimmed to what a single fixed-`assetId` page needs: no multi-item arrows/
 * keyboard nav, no search-panel item picker, no "create metadata for a schema that doesn't exist
 * yet" picker, no "Remove All Metadata" modal, no title banner/gear. Edit/Cancel/Save and the
 * search box ARE wired, since those are genuinely `MetadataViewerSection`'s/`useEditControl`'s
 * own behavior, not page chrome. `activeTabs` IS real state (not a stub) — the backend can now
 * return more than one view under a configured category (`CS_METADATA_VIEWER_CATEGORY`), so a
 * real tab bar needs somewhere to keep the selection, same shape `ImportModal`/
 * `CreatePlaceholderPage` already use.
 */
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const SearchArea = styled.div`
  flex: 1;
  max-width: 320px;
`;

const MetadataViewerPanel = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const [search, setSearch] = useState("");
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});

  const { data, isLoading, error } = useItemMetadata(assetId);
  // `useEditControl`'s `queryKey` prop is `any[]` (verbatim from Cantemo's source) — spread out
  // of the readonly tuple `cs_metadata_viewerQueryKeys.item` returns for `useQuery`.
  const queryKey = [...cs_metadata_viewerQueryKeys.item(assetId ?? "")];

  const collapsedControl = useCollapsedControl({
    search,
    timespans: data?.timespans,
    mapping: data?.mapping,
  });

  const editControl = useEditControl({
    type: "item",
    itemId: assetId ?? "",
    schema: data?.timespans[0]?.schema,
    timespans: data?.timespans,
    mapping: data?.mapping,
    hasMetadata: data?.hasMetadata,
    views: data?.views,
    enabled: !!data && data.permissions.write,
    queryKey,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      await editControl?.saveValues(editControl.values);
      editControl?.cancelEditing();
    },
  });

  const components = buildComponents({
    timespans: data?.timespans,
    search,
    data,
    activeTabs,
    collapsedControl,
    pendingGroups: editControl?.pendingGroups,
    removedGroups: editControl?.removedGroups,
    editValues: editControl?.values,
    isEditing: editControl?.isEditingKey("/"),
    mode: "generic",
    canEdit: !!editControl,
  });

  if (!assetId) {
    return <NotFoundPage />;
  }

  return (
    <PageWrapper>
      <FeatureGate isLoading={isLoading} error={error} enabled={!!data}>
        <PageTitle title={data?.title ?? "Metadata"} noPadding />
        <Toolbar>
          <SearchArea>
            <Input
              value={search}
              onChange={setSearch}
              placeholder="Search Metadata"
              icon={Search}
              clearable
            />
          </SearchArea>
          {editControl &&
            (editControl.isEditing ? (
              <>
                <Button
                  label="Cancel"
                  icon={X}
                  type="danger"
                  disabled={editControl.isSaving}
                  borderless
                  onClick={() => {
                    editControl.cancelEditing();
                    editControl.setValues([]);
                  }}
                />
                <Button
                  label="Save"
                  icon={Check}
                  type="success"
                  loading={saveMutation.isLoading}
                  disabled={!editControl.canSave || editControl.isSaving}
                  tooltip={
                    editControl.isError
                      ? "Fix errors before saving"
                      : !editControl.canSave
                      ? "No changes"
                      : undefined
                  }
                  onClick={() => saveMutation.mutate()}
                />
              </>
            ) : (
              (editControl.hasMetadata
                ? data?.permissions.write
                : data?.permissions.create) && (
                <Button
                  label={editControl.hasMetadata ? "Edit" : "Create"}
                  type="default"
                  onClick={() => editControl.setEditingKey("/", true)}
                />
              )
            ))}
        </Toolbar>
        {saveMutation.isError && <Banner error="Failed to save changes" />}
        {isLoading ? (
          <Spinner />
        ) : !components.length ? (
          <Banner
            placeholder={search ? "No search results found" : "No metadata"}
            padTop
          />
        ) : (
          components.map((section) => (
            <MetadataViewerSection
              key={section.editKey}
              data={data}
              section={section}
              search={search}
              collapsedControl={collapsedControl}
              activeTabs={activeTabs}
              setActiveTab={(schema, tab) =>
                setActiveTabs((prev) => ({ ...prev, [schema]: tab }))
              }
              editControl={editControl}
            />
          ))
        )}
      </FeatureGate>
    </PageWrapper>
  );
};

export default MetadataViewerPanel;
